import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { DEALS_QUERY_KEY } from './useDeals';
import { type ApiDealResponse } from './types';

/**
 * Corpo do PUT /api/deals/{id} (UpdateDealBody do backend). O `id` vai na rota e o `RowVersion` no
 * header If-Match; o resto dos campos editáveis vai no corpo. `legalStatus` é o valor NUMÉRICO do
 * enum (bind por número — sem JsonStringEnumConverter), igual ao toggle de prioritário.
 *
 * Empresa-alvo é opcional (item 1): targetCompanyId pode ser null. A Originadora é editável (item 8);
 * pessoa originadora e internalOriginatorId seguem reenviados inalterados.
 */
export interface UpdateDealBody {
  codename: string;
  legalStatus: number;
  folderId: string | null;
  targetCompanyId: string | null;
  originatorIdSnapshot: string | null;
  originatorPersonIdSnapshot: string | null;
  internalOriginatorId: string | null;
  leadUserId: string;
  investmentValueBRL: number | null;
  /** Split do cheque (Item 3): total = discricionário + coinvestimento (validado no backend). */
  checkDiscretionaryBRL: number | null;
  checkCoinvestmentBRL: number | null;
  thesisSummary: string | null;
  isPriority: boolean;
  thesisIds: number[];
  teamUserIds: string[];
  tags: string[];
}

/**
 * Salva a edição da ficha (PUT /api/deals/{id} + If-Match). Diferente do toggle de prioritário, o
 * corpo completo já vem montado pelo formulário (que tem o detalhe carregado), então não refazemos o
 * GET. Em sucesso: grava a resposta no cache do detalhe (['deal', id]) — atualiza ETag/campos — e
 * invalida a listagem pra refletir os campos enriquecidos (nome do líder/teses/equipe, aging).
 * 412 (alguém editou no meio) → toast + recarrega detalhe e lista pra o usuário reabrir com dado
 * novo. O fechamento do formulário de edição nesse caso é responsabilidade do chamador (achado F1,
 * revisão 2026-07 — ver o onError passado pelo DealEditForm em cada `mutate`), pra não permitir um
 * 2º "Salvar" com dado velho depois que o ETag da prop já mudou.
 */
export function useUpdateDeal() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, body }: { id: string; etag: string; body: UpdateDealBody }) =>
      apiFetch<ApiDealResponse>(`/api/deals/${id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${etag}"` },
        body,
      }),
    onSuccess: (resp) => {
      qc.setQueryData(['deal', resp.id], resp);
      void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      toast('Ficha atualizada.');
    },
    onError: (err, vars) => {
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa enquanto você editava. Reabra a edição para ver os dados atuais.', 'warn');
        void qc.invalidateQueries({ queryKey: ['deal', vars.id] });
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível salvar as alterações.', 'danger');
      }
    },
  });
}
